// ============================================================
// FederationTab.js
// Federation tab inside <TenantDetailPage>. Four-state UI driven
// by deriveFederationState (not_configured | pending_consent |
// active | needs_reconsent) with create / edit / consent-URL /
// test-connection / deactivate actions, plus per-domain add /
// remove for the 1:N TenantFederationDomains child table.
//
// W3.5 1:N refactor — see CLAUDE.md Gotcha #41. Domains are
// managed:
//   - In bulk at create time via DomainChipInput.
//   - Individually post-create via AddDomainDialog and inline
//     delete actions on the active-state domain list.
//   - The edit form NO LONGER carries a domain field — domain
//     mutations go through the dedicated endpoints exclusively,
//     enforced server-side (400 use_domains_endpoints).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-tenants-federation-*.js
//   docs/operations/app-registrations-runbook.md §G
//
// CRITICAL STATE COUPLING — Test Connection consent_revoked:
//   When testConnection returns { success: false, error: 'consent_revoked' },
//   the backend has already flipped TenantFederationConfig.NeedsReconsent=1.
//   The handler immediately re-fetches the config so deriveFederationState
//   transitions the badge from 'active' to 'needs_reconsent' in place.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, AlertCircle, RefreshCw, Plus, Trash2, Copy } from 'lucide-react';
import Field from '../components/Field';
import ConfirmDialog from '../components/ConfirmDialog';
import ConsentUrlDialog from '../components/ConsentUrlDialog';
import DomainChipInput from '../components/DomainChipInput';
import AddDomainDialog from '../components/AddDomainDialog';
import { FederationStatusBadge } from '../components/StatusBadge';
import {
  getTenantFederation,
  createTenantFederation,
  updateTenantFederation,
  generateConsentUrl,
  testConnection,
  addFederationDomain,
  deleteFederationDomain,
  verifyWorkforceApiSp,
  resetFederationConsent,
} from '../api/federation';
import { listClients } from '../api/clients';
import { generateM365LifecycleConsentUrl } from '../api/m365Lifecycle';
import { generateEmergencyPackConsentUrl } from '../api/emergencyPack';
import { deriveFederationState } from '../utils/federationState';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import { slugifyClientName, sanitizeSlugInput, fillGroupsScript } from '../utils/carehubGroupsScript';
import { toast } from '../components/Toaster';
import StatusMessage from '../components/StatusMessage';
import './FederationTab.css';

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
// A2Z IT (the platform's own home tenant). A client tenant can never be this —
// self-federation is nonsensical and Gotcha #38-adjacent. Guard the UI.
const PLATFORM_HOME_TENANT_ID = '7fa4f7ce-0785-4395-b939-d589268a4a74';

const EMPTY_FORM = { externalTenantId: '', federatedDomains: [] };

function validateExternalTenantId(value) {
  if (!value) return 'Required';
  if (!GUID_REGEX.test(value)) return 'Must be a valid GUID';
  if (value.toLowerCase() === PLATFORM_HOME_TENANT_ID) return "Cannot federate the platform's own tenant";
  return null;
}

function validateDomain(value) {
  if (value.startsWith('http://') || value.startsWith('https://')) return 'Domain only — no protocol prefix';
  if (!DOMAIN_REGEX.test(value)) return 'Not a valid email domain';
  return null;
}

export default function FederationTab({ tenantId, tenantExternalTenantId }) {
  // ── Lifecycle / fetch state ────────────────────────────────
  const [config, setConfig]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);

  // ── Mode: 'view' | 'creating' | 'editing' ───────────────────
  const [mode, setMode] = useState('view');

  // ── In-flight async indicator (single at a time) ───────────
  const [inFlight, setInFlight] = useState(null); // 'generating' | 'testing' | 'updating' | 'deactivating' | 'reactivating' | 'adding' | 'deleting' | null

  // ── Test Connection result (cleared on consent_revoked re-fetch) ──
  const [testResult, setTestResult] = useState(null);

  // ── Consent URL dialog state ───────────────────────────────
  const [consentUrlDialog, setConsentUrlDialog] = useState({ open: false, url: null });

  // ── M365 Identity Lifecycle consent URL dialog (Platform-03) ──
  const [m365ConsentDialog, setM365ConsentDialog] = useState({ open: false, url: null });

  // ── Emergency Pack (Document Auth) consent URL dialog (BC phase 2) ──
  const [emergencyPackConsentDialog, setEmergencyPackConsentDialog] = useState({ open: false, url: null });

  // ── Onboarding artifacts (3-step checklist) ───────────────
  // Fetched on mount whenever state != not_configured, via
  // generateConsentUrl(). Body carries: consentUrl (Federation
  // Workload, d16fc04a), federatedSignInConsentUrl (Federated SPA,
  // e9facbbc), workforceApiSpProvisioningCommand (`az ad sp create
  // --id <FEDERATED_API_AUDIENCE>`). Loading/error are surfaced
  // inline next to the corresponding step. CLAUDE.md Gotcha #51.
  const [artifacts, setArtifacts] = useState(null);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [artifactsError, setArtifactsError] = useState(null);

  // ── Workforce API SP pre-flight verify result ──────────────
  // null = not yet verified; { spExists: true } = present;
  // { spExists: false, error?: ... } = absent or probe failure.
  const [spVerify, setSpVerify] = useState(null);
  const [spVerifying, setSpVerifying] = useState(false);

  // ── Step 4 (create role groups) — tenant clients + slug field ──
  // The onboarding sequence is per-TENANT, but role groups are per-
  // CLIENT (a shared tenant, e.g. Supporting Care, has several clients,
  // each needing its own group set / slug). We fetch the tenant's
  // clients to (a) pre-fill the slug from the single client's name and
  // (b) drive a multi-client reminder. null = not yet loaded.
  const [clients, setClients] = useState(null);
  const [groupsSlugInput, setGroupsSlugInput] = useState('');

  // ── Deactivate confirmation dialog ─────────────────────────
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [resetConsentOpen, setResetConsentOpen] = useState(false);

  // ── Add-domain dialog ──────────────────────────────────────
  const [addDomainOpen, setAddDomainOpen] = useState(false);

  // ── Delete-domain confirmation (per-row) ───────────────────
  const [deleteDomainTarget, setDeleteDomainTarget] = useState(null); // { domainId, domain } | null
  const [deleteDomainError, setDeleteDomainError] = useState(null);

  // ── Form state (shared by create + edit) ───────────────────
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // ── Initial fetch ──────────────────────────────────────────
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getTenantFederation(tenantId);
      setConfig(data);
    } catch (err) {
      if (err && err.status === 404) {
        setConfig(null);
      } else {
        setLoadError(translateError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const state = deriveFederationState(config);

  // ── Auto-fetch onboarding artifacts when state ≠ not_configured ──
  // generateConsentUrl is read-only on the backend (just synthesises
  // URLs + the SP-provisioning command from config + env vars) — safe
  // to fire on mount. Re-fetches when the state transitions out of
  // not_configured (e.g. immediately after a successful create).
  useEffect(() => {
    if (state === 'not_configured') {
      setArtifacts(null);
      setArtifactsError(null);
      return;
    }
    let cancelled = false;
    setArtifactsLoading(true);
    setArtifactsError(null);
    generateConsentUrl(tenantId)
      .then((data) => { if (!cancelled) setArtifacts(data); })
      .catch((err) => { if (!cancelled) setArtifactsError(translateError(err)); })
      .finally(() => { if (!cancelled) setArtifactsLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, state]);

  // ── Fetch the tenant's clients for Step 4 (create role groups) ──
  // Drives the multi-client reminder + the single-client slug pre-fill.
  // Failure is non-fatal — the operator can still type a slug manually,
  // so a fetch error just leaves the reminder off + the field empty.
  useEffect(() => {
    if (state === 'not_configured') { setClients(null); return; }
    let cancelled = false;
    listClients({ tenantId })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data && data.clients) ? data.clients : [];
        setClients(list);
        // Pre-fill only when there's exactly one client (a multi-client
        // tenant has no single correct slug) AND the operator hasn't
        // already typed one (functional guard preserves their edit).
        if (list.length === 1) {
          setGroupsSlugInput((prev) => prev || slugifyClientName(list[0].clientName));
        }
      })
      .catch(() => { if (!cancelled) setClients([]); });
    return () => { cancelled = true; };
  }, [tenantId, state]);

  async function handleVerifyWorkforceApiSp() {
    setSpVerifying(true);
    setSpVerify(null);
    try {
      const result = await verifyWorkforceApiSp(tenantId);
      // Verifier is pure-read on the backend (Gotcha #51 — never
      // flips NeedsReconsent, never emits consent_revoked audit).
      // So no federation config refetch is needed on any error code;
      // permission_not_granted / graph_forbidden / transient / unknown
      // all surface inline against an unchanged badge state. The
      // operator clicks step 2 of the onboarding checklist (or fixes
      // the underlying issue) and re-clicks Verify — no backend
      // lifecycle state changed by the verify probe itself.
      setSpVerify(result);
    } catch (err) {
      setSpVerify({ spExists: null, error: 'unknown', message: translateError(err) });
    } finally {
      setSpVerifying(false);
    }
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard.');
    } catch (_err) {
      toast.error('Copy failed — select and copy manually.');
    }
  }

  // ── Form helpers ───────────────────────────────────────────
  // Pre-populate `externalTenantId` from the parent Tenant row when
  // it's known (the Tenants table carries the directory ID as a
  // first-class field). Shaves one copy-paste off every federation
  // onboarding; the operator can still edit the value before submit
  // if the federated tenant ID differs from the Tenant row's
  // externalTenantId (rare — in practice they always match).
  function resetFormForCreate() {
    setForm({
      ...EMPTY_FORM,
      externalTenantId: tenantExternalTenantId || '',
    });
    setFormErrors({});
    setMode('creating');
  }

  function resetFormForEdit() {
    setForm({
      externalTenantId: config.externalTenantId || '',
      isActive: config.isActive,
    });
    setFormErrors({});
    setMode('editing');
  }

  function cancelForm() {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setMode('view');
  }

  function updateFormField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  // ── Map API errors → form-level errors ─────────────────────
  function applyApiError(err) {
    const status = err && err.status;
    const code   = err && err.error;
    const detail = err && err.detail;

    if (status === 400) {
      if (code === 'invalid_external_tenant_id') {
        setFormErrors({ externalTenantId: detail || 'Invalid client tenant ID' });
        return;
      }
      if (code === 'invalid_domain_format' || code === 'invalid_federated_domains' || code === 'duplicate_domain_in_request' || code === 'too_many_domains') {
        setFormErrors({ federatedDomains: detail || 'Invalid domain' });
        return;
      }
      if (code === 'immutable_field') {
        setFormErrors({ _top: 'Server rejected an immutable field. Refresh and try again.' });
        return;
      }
      if (code === 'use_domains_endpoints') {
        // Should not happen now that the form omits the domain field; defensive.
        setFormErrors({ _top: 'Refresh the page — domains are managed through the per-domain controls.' });
        return;
      }
      setFormErrors({ _top: detail || translateError(err) });
      return;
    }

    if (status === 409 && code === 'federated_domain_already_configured') {
      const existingTenantId = err && (err.existingTenantId || (err.body && err.body.existingTenantId));
      const conflictingDomain = err && (err.conflictingDomain || (err.body && err.body.conflictingDomain));
      setFormErrors({
        federatedDomains: conflictingDomain
          ? `Already configured: '${conflictingDomain}'`
          : 'A domain is already configured for another tenant',
        existingTenantId,
      });
      return;
    }

    if (status === 409 && code === 'federation_config_exists') {
      setFormErrors({ _top: 'Federation is already configured for this tenant. Refresh the page to see the current configuration.' });
      return;
    }

    setFormErrors({ _top: translateError(err) });
  }

  // ── Action handlers ────────────────────────────────────────

  async function handleCreate(e) {
    if (e) e.preventDefault();
    const errs = {};
    const extErr = validateExternalTenantId(form.externalTenantId);
    if (extErr) errs.externalTenantId = extErr;
    if (!Array.isArray(form.federatedDomains) || form.federatedDomains.length === 0) {
      errs.federatedDomains = 'At least one domain required';
    }
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    setInFlight('updating');
    try {
      const created = await createTenantFederation(tenantId, {
        externalTenantId: form.externalTenantId.toLowerCase(),
        identityProviderType: 'm365',
        federatedDomains: form.federatedDomains,
      });
      setConfig(created);
      setMode('view');
      setForm(EMPTY_FORM);
      setFormErrors({});
      toast.success('Federation configuration created. Generate a consent URL to begin client admin consent flow.');
    } catch (err) {
      applyApiError(err);
    } finally {
      setInFlight(null);
    }
  }

  async function handleUpdate(e) {
    if (e) e.preventDefault();
    if (!config) return;

    const patch = {};
    if (form.externalTenantId !== config.externalTenantId) {
      const errMsg = validateExternalTenantId(form.externalTenantId);
      if (errMsg) { setFormErrors({ externalTenantId: errMsg }); return; }
      patch.externalTenantId = form.externalTenantId.toLowerCase();
    }
    if (form.isActive !== config.isActive) {
      patch.isActive = form.isActive;
    }
    if (Object.keys(patch).length === 0) {
      setMode('view');
      return;
    }

    setInFlight('updating');
    try {
      const updated = await updateTenantFederation(tenantId, patch);
      // The PATCH response carries the config fields but no domains —
      // preserve the existing domains array in local state so the
      // view-mode list keeps rendering.
      setConfig((prev) => ({ ...updated, federatedDomains: prev ? prev.federatedDomains : [] }));
      setMode('view');
      setForm(EMPTY_FORM);
      setFormErrors({});
      toast.success('Federation configuration updated.');
    } catch (err) {
      applyApiError(err);
    } finally {
      setInFlight(null);
    }
  }

  async function handleGenerateConsentUrl() {
    setInFlight('generating');
    try {
      const { consentUrl } = await generateConsentUrl(tenantId);
      setConsentUrlDialog({ open: true, url: consentUrl });
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleGenerateM365ConsentUrl() {
    setInFlight('generatingM365');
    try {
      const { consentUrl } = await generateM365LifecycleConsentUrl(tenantId);
      setM365ConsentDialog({ open: true, url: consentUrl });
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleGenerateEmergencyPackConsentUrl() {
    setInFlight('generatingEmergencyPack');
    try {
      const { consentUrl } = await generateEmergencyPackConsentUrl(tenantId);
      setEmergencyPackConsentDialog({ open: true, url: consentUrl });
    } catch (err) {
      // While Part A of the ceremony is not done, this is a clean 409 with
      // the runbook pointer — surfaced as-is, never dressed as success.
      toast.error(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleTestConnection() {
    setInFlight('testing');
    setTestResult(null);
    try {
      const result = await testConnection(tenantId);
      if (result && result.success === false && result.error === 'consent_revoked') {
        try {
          const refreshed = await getTenantFederation(tenantId);
          setConfig(refreshed);
          setTestResult(null);
        } catch (refreshErr) {
          setTestResult(result);
          toast.error('Connection probe detected consent revocation, but the federation row could not be refreshed. Reload the page.');
        }
      } else {
        setTestResult(result);
      }
    } catch (err) {
      setTestResult({ success: false, error: 'unknown', message: translateError(err) });
    } finally {
      setInFlight(null);
    }
  }

  // Send the tenant back to awaiting-consent. Same state-coupling as the
  // consent_revoked branch of handleTestConnection: the server has changed
  // NeedsReconsent, so re-fetch the config and let deriveFederationState move
  // the badge — never patch local state by hand, or the badge and the row
  // disagree.
  async function handleResetConsent() {
    setInFlight('resettingConsent');
    try {
      await resetFederationConsent(tenantId);
      const refreshed = await getTenantFederation(tenantId);
      setConfig(refreshed);
      // A stale "connection OK" panel under a now-awaiting-consent badge would
      // be actively misleading.
      setTestResult(null);
      setResetConsentOpen(false);
      toast.success('Consent reset. Generate a new consent URL for the client administrator.');
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleDeactivate() {
    setInFlight('deactivating');
    try {
      const updated = await updateTenantFederation(tenantId, { isActive: false });
      // Preserve existing domains in local state (PATCH response omits them).
      setConfig((prev) => ({ ...updated, federatedDomains: prev ? prev.federatedDomains : [] }));
      setDeactivateOpen(false);
      toast.success('Federation deactivated.');
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  async function handleReactivate() {
    setInFlight('reactivating');
    try {
      const updated = await updateTenantFederation(tenantId, { isActive: true });
      // Preserve existing domains in local state (PATCH response omits them).
      setConfig((prev) => ({ ...updated, federatedDomains: prev ? prev.federatedDomains : [] }));
      toast.success('Federation reactivated.');
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Per-domain add/delete ──────────────────────────────────

  async function handleAddDomain(domain) {
    // Optimistic update — append the chip; rollback on error.
    const optimisticChip = { domainId: -Date.now(), domain, createdAt: new Date().toISOString(), createdBy: 'you', __optimistic: true };
    setConfig((prev) => ({
      ...prev,
      federatedDomains: [...(prev.federatedDomains || []), optimisticChip],
    }));
    setInFlight('adding');
    try {
      const added = await addFederationDomain(tenantId, domain);
      setConfig((prev) => ({
        ...prev,
        federatedDomains: (prev.federatedDomains || [])
          .map((d) => (d.__optimistic && d.domain === domain ? added : d)),
      }));
      setAddDomainOpen(false);
      toast.success(`Domain '${domain}' added.`);
    } catch (err) {
      // Rollback the optimistic chip.
      setConfig((prev) => ({
        ...prev,
        federatedDomains: (prev.federatedDomains || []).filter((d) => !(d.__optimistic && d.domain === domain)),
      }));
      // Re-throw so AddDomainDialog can render the inline error.
      throw err;
    } finally {
      setInFlight(null);
    }
  }

  async function handleConfirmDeleteDomain() {
    if (!deleteDomainTarget) return;
    const { domainId, domain } = deleteDomainTarget;
    setDeleteDomainError(null);

    // Optimistic remove.
    const snapshot = config.federatedDomains;
    setConfig((prev) => ({
      ...prev,
      federatedDomains: (prev.federatedDomains || []).filter((d) => d.domainId !== domainId),
    }));
    setInFlight('deleting');
    try {
      await deleteFederationDomain(tenantId, domainId);
      setDeleteDomainTarget(null);
      toast.success(`Domain '${domain}' removed.`);
    } catch (err) {
      // Rollback.
      setConfig((prev) => ({ ...prev, federatedDomains: snapshot }));
      const status = err && err.status;
      const code   = err && err.error;
      if (status === 400 && code === 'cannot_remove_last_domain') {
        setDeleteDomainError('At least one domain must remain. Add a replacement domain before removing this one.');
        return;
      }
      setDeleteDomainError(translateError(err));
    } finally {
      setInFlight(null);
    }
  }

  // ── Loading / error ────────────────────────────────────────
  if (loading) {
    return <StatusMessage variant="loading" className="federation-tab__state">Loading federation configuration…</StatusMessage>;
  }
  if (loadError) {
    return (
      <StatusMessage variant="error" className="federation-tab__state federation-tab__state--error">
        <p>{loadError}</p>
        <button type="button" className="federation-tab__retry" onClick={fetchConfig}>
          Retry
        </button>
      </StatusMessage>
    );
  }

  if (mode === 'creating') {
    return renderCreateForm({
      form,
      formErrors,
      inFlight,
      onChange: updateFormField,
      onSubmit: handleCreate,
      onCancel: () => { setForm(EMPTY_FORM); setFormErrors({}); setMode('view'); },
    });
  }

  if (mode === 'editing') {
    return renderEditForm({
      form,
      formErrors,
      inFlight,
      initialConfig: config,
      onChange: updateFormField,
      onSubmit: handleUpdate,
      onCancel: cancelForm,
    });
  }

  return (
    <div className="federation-tab">
      {state === 'not_configured' && renderNotConfigured({ onConfigure: resetFormForCreate })}
      {state !== 'not_configured' && renderConfigView({
        config,
        state,
        inFlight,
        testResult,
        artifacts,
        artifactsLoading,
        artifactsError,
        spVerify,
        spVerifying,
        clients,
        groupsSlugInput,
        onGroupsSlugChange: setGroupsSlugInput,
        onGenerateConsentUrl: handleGenerateConsentUrl,
        onGenerateM365ConsentUrl: handleGenerateM365ConsentUrl,
        onGenerateEmergencyPackConsentUrl: handleGenerateEmergencyPackConsentUrl,
        onTestConnection: handleTestConnection,
        onVerifyWorkforceApiSp: handleVerifyWorkforceApiSp,
        onCopy: copyToClipboard,
        onEdit: resetFormForEdit,
        onDeactivate: () => setDeactivateOpen(true),
        onResetConsent: () => setResetConsentOpen(true),
        onReactivate: handleReactivate,
        onClearTestResult: () => setTestResult(null),
        onAddDomain: () => setAddDomainOpen(true),
        onDeleteDomain: (row) => { setDeleteDomainError(null); setDeleteDomainTarget(row); },
      })}

      {consentUrlDialog.open && (
        <ConsentUrlDialog
          consentUrl={consentUrlDialog.url}
          onClose={() => setConsentUrlDialog({ open: false, url: null })}
        />
      )}

      {m365ConsentDialog.open && (
        <ConsentUrlDialog
          consentUrl={m365ConsentDialog.url}
          title="Microsoft 365 Identity Lifecycle consent URL"
          intro="Share this URL with the client tenant's M365 Global Administrator. They grant admin consent for the A2Z CareHub Identity Lifecycle app (Work Account provisioning, licensing, and offboarding). Consent alone activates nothing — the kill switch, per-client capability, and tenant allowlist remain fail-closed."
          onClose={() => setM365ConsentDialog({ open: false, url: null })}
        />
      )}

      {emergencyPackConsentDialog.open && (
        <ConsentUrlDialog
          consentUrl={emergencyPackConsentDialog.url}
          title="Emergency Pack (Document Auth) consent URL"
          intro="Share this URL with the client tenant's M365 Global Administrator. They grant admin consent for the A2Z CareHub Document Auth app (Sites.Selected) — Part B of the emergency-pack setup ceremony. Consent alone reaches nothing: the app can access no site until the per-site grant is made (Part C), and the kill switch, tenant allowlist and per-client row all remain fail-closed."
          onClose={() => setEmergencyPackConsentDialog({ open: false, url: null })}
        />
      )}

      <AddDomainDialog
        isOpen={addDomainOpen}
        onClose={() => setAddDomainOpen(false)}
        onSubmit={handleAddDomain}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteDomainTarget)}
        onClose={() => { if (inFlight !== 'deleting') { setDeleteDomainTarget(null); setDeleteDomainError(null); } }}
        onConfirm={handleConfirmDeleteDomain}
        title="Remove federated domain"
        message={
          deleteDomainTarget ? (
            <>
              <p>
                Remove <strong>{deleteDomainTarget.domain}</strong> from this federation config?
                Federated users signing in with this email domain will be denied. This action
                does not revoke consent. Continue?
              </p>
              {deleteDomainError && (
                <p className="federation-tab__form-error" role="alert" style={{ marginTop: '0.75rem' }}>
                  {deleteDomainError}
                </p>
              )}
            </>
          ) : ''
        }
        confirmLabel="Remove domain"
        cancelLabel="Keep domain"
        tone="danger"
        isSubmitting={inFlight === 'deleting'}
      />

      <ConfirmDialog
        isOpen={resetConsentOpen}
        onClose={() => { if (inFlight !== 'resettingConsent') setResetConsentOpen(false); }}
        onConfirm={handleResetConsent}
        title="Reset federation consent"
        message={
          <>
            <p>
              This marks the tenant as awaiting consent again, so the portal will offer a new
              consent URL. Use it when the grant has been invalidated on the client side — the
              enterprise application deleted, the service principal removed, or consent revoked.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              Federation stays <strong>active</strong> and existing sign-ins are not cut off. What
              changes is that the client administrator must grant consent again before the next
              renewal of access.
            </p>
          </>
        }
        confirmLabel="Reset consent"
        cancelLabel="Cancel"
        tone="danger"
        isSubmitting={inFlight === 'resettingConsent'}
      />

      <ConfirmDialog
        isOpen={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivate}
        title="Deactivate federation"
        message="Deactivate federation for this tenant? Users signing in via federation will be blocked. This does not delete the configuration — you can re-activate later."
        confirmLabel="Deactivate"
        cancelLabel="Keep active"
        tone="danger"
        isSubmitting={inFlight === 'deactivating'}
      />
    </div>
  );
}

// ── View sub-renders ─────────────────────────────────────────

function renderNotConfigured({ onConfigure }) {
  return (
    <div className="federation-tab__cta-card">
      <h2 className="federation-tab__cta-title">Federation is not configured for this tenant.</h2>
      <p className="federation-tab__cta-subtitle">
        Configure federation to let users from a federated M365 tenant sign in via SSO.
      </p>
      <button
        type="button"
        className="button-primary"
        onClick={onConfigure}
      >
        Configure federation
      </button>
    </div>
  );
}

function renderConfigView({
  config, state, inFlight, testResult,
  artifacts, artifactsLoading, artifactsError,
  spVerify, spVerifying,
  clients, groupsSlugInput, onGroupsSlugChange,
  onGenerateConsentUrl, onGenerateM365ConsentUrl, onGenerateEmergencyPackConsentUrl, onTestConnection, onVerifyWorkforceApiSp, onCopy,
  onEdit, onDeactivate, onResetConsent, onReactivate, onClearTestResult,
  onAddDomain, onDeleteDomain,
}) {
  const domains = Array.isArray(config.federatedDomains) ? config.federatedDomains : [];
  const deactivated = state === 'deactivated';

  return (
    <div className="federation-tab__view">
      <div className="federation-tab__view-header">
        <FederationStatusBadge state={state} />
      </div>

      {deactivated && (
        <div className="federation-tab__warning" role="status">
          <AlertTriangle size={16} />
          <span>
            Federation is deactivated. Federated users are blocked from signing in.
            The configuration is preserved — reactivate to restore access.
          </span>
        </div>
      )}

      {state === 'needs_reconsent' && (
        <div className="federation-tab__warning" role="alert">
          <AlertTriangle size={16} />
          <span>
            Admin consent has been revoked or is no longer valid. The client tenant administrator
            must re-consent before federated users can sign in.
          </span>
        </div>
      )}

      <dl className="federation-tab__readonly">
        <dt>Client tenant directory ID</dt>
        <dd>{config.externalTenantId}</dd>

        <dt>Identity provider</dt>
        <dd>Microsoft 365</dd>

        <dt>Created</dt>
        <dd>{formatDate(config.createdAt)} by {config.createdBy}</dd>

        {state === 'active' && config.consentGrantedAt && (
          <>
            <dt>Consent granted</dt>
            <dd>{formatDate(config.consentGrantedAt)}</dd>
          </>
        )}
      </dl>

      {renderOnboardingChecklist({
        artifacts, artifactsLoading, artifactsError,
        spVerify, spVerifying,
        clients, groupsSlugInput, onGroupsSlugChange,
        targetTenantId: config.externalTenantId,
        onVerifyWorkforceApiSp, onCopy,
      })}

      <div className="federation-tab__domains">
        <div className="federation-tab__domains-header">
          <h3 className="federation-tab__domains-title">Federated domains</h3>
          <button
            type="button"
            className="button-secondary"
            onClick={onAddDomain}
          >
            <Plus size={14} /> Add domain
          </button>
        </div>

        {domains.length === 0 ? (
          <p className="federation-tab__hint">
            No domains configured. Federated sign-in will not resolve until at least one
            domain is added.
          </p>
        ) : (
          <table className="federation-tab__domains-table" data-testid="federation-domains-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Added</th>
                <th>By</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.domainId}>
                  <td className="federation-tab__domains-domain">{d.domain}</td>
                  <td className="federation-tab__domains-muted">{formatDate(d.createdAt)}</td>
                  <td className="federation-tab__domains-muted">{d.createdBy}</td>
                  <td className="federation-tab__domains-actions">
                    <button
                      type="button"
                      className="federation-tab__domains-delete"
                      onClick={() => onDeleteDomain({ domainId: d.domainId, domain: d.domain })}
                      aria-label={`Remove ${d.domain}`}
                      disabled={inFlight === 'deleting' || inFlight === 'adding'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <section className="federation-tab__onboarding" aria-labelledby="m365-lifecycle-heading">
        <h3 className="federation-tab__onboarding-title" id="m365-lifecycle-heading">
          Microsoft 365 Identity Lifecycle
        </h3>
        <p className="federation-tab__onboarding-intro">
          Admin-consent URL for the A2Z CareHub <strong>Identity Lifecycle</strong> app —
          the reg that provisions Work Accounts (email &amp; sign-in), assigns licences, and
          disables/removes accounts on offboarding. The client tenant&apos;s Global Administrator
          clicks it once. The URL carries the correct redirect + correlation state. Re-generate
          and re-consent whenever the app&apos;s Graph permissions change. Consent alone activates
          nothing — enablement is a separate A2Z IT step (kill switch + per-client capability +
          tenant allowlist all fail-closed).
        </p>
        <button
          type="button"
          className="button-secondary"
          onClick={onGenerateM365ConsentUrl}
          disabled={inFlight === 'generatingM365'}
          data-testid="generate-m365-lifecycle-consent-url"
        >
          {inFlight === 'generatingM365' ? 'Generating…' : 'Generate Identity Lifecycle consent URL'}
        </button>
      </section>

      <section className="federation-tab__onboarding" aria-labelledby="emergency-pack-heading">
        <h3 className="federation-tab__onboarding-title" id="emergency-pack-heading">
          Emergency Pack (Document Auth)
        </h3>
        <p className="federation-tab__onboarding-intro">
          Admin-consent URL for the A2Z CareHub <strong>Document Auth</strong> app — the
          Sites.Selected reg that pushes the nightly emergency care pack into the client&apos;s
          own SharePoint site. Part B of the setup ceremony
          (docs/operations/emergency-pack-spo-setup.md). Consent alone reaches nothing: the app
          can access no site until the per-site grant is made (Part C), and the kill switch,
          tenant allowlist and per-client row all remain fail-closed. Until the reg exists
          (Part A), generating returns a clear refusal pointing at the runbook.
        </p>
        <button
          type="button"
          className="button-secondary"
          onClick={onGenerateEmergencyPackConsentUrl}
          disabled={inFlight === 'generatingEmergencyPack'}
          data-testid="generate-emergency-pack-consent-url"
        >
          {inFlight === 'generatingEmergencyPack' ? 'Generating…' : 'Generate Emergency Pack consent URL'}
        </button>
      </section>

      <div className="federation-tab__actions">
        {(state === 'pending_consent' || state === 'needs_reconsent') && (
          <button
            type="button"
            className="button-primary"
            onClick={onGenerateConsentUrl}
            disabled={inFlight === 'generating'}
          >
            {state === 'needs_reconsent' ? 'Generate new consent URL' : 'Generate consent URL'}
          </button>
        )}

        {state === 'active' && (
          <button
            type="button"
            className="button-primary"
            onClick={onTestConnection}
            disabled={inFlight === 'testing'}
          >
            {inFlight === 'testing' ? 'Testing…' : 'Test Connection'}
          </button>
        )}

        {state === 'active' && (
          <button
            type="button"
            className="button-secondary"
            onClick={onGenerateConsentUrl}
            disabled={inFlight === 'generating'}
          >
            Generate new consent URL
          </button>
        )}

        {/* Only offered in the ACTIVE state. In pending_consent and
            needs_reconsent the tenant is already awaiting consent, so the
            button would be a no-op dressed as an action. */}
        {state === 'active' && (
          <button
            type="button"
            className="button-secondary"
            onClick={onResetConsent}
            disabled={inFlight === 'resettingConsent'}
          >
            {inFlight === 'resettingConsent' ? 'Resetting…' : 'Reset consent'}
          </button>
        )}

        {state === 'needs_reconsent' && (
          <button
            type="button"
            className="button-secondary"
            onClick={onTestConnection}
            disabled={inFlight === 'testing'}
          >
            {inFlight === 'testing' ? 'Testing…' : 'Test Connection'}
          </button>
        )}

        <button
          type="button"
          className="button-secondary"
          onClick={onEdit}
        >
          Edit
        </button>
        {deactivated ? (
          <button
            type="button"
            className="button-primary"
            onClick={onReactivate}
            disabled={inFlight === 'reactivating'}
          >
            {inFlight === 'reactivating' ? 'Reactivating…' : 'Reactivate'}
          </button>
        ) : (
          <button
            type="button"
            className="button-secondary federation-tab__deactivate"
            onClick={onDeactivate}
          >
            Deactivate
          </button>
        )}
      </div>

      {state === 'pending_consent' && (
        <p className="federation-tab__hint">
          Awaiting admin consent from the client tenant. Once the client tenant administrator
          completes the consent flow via the generated URL, this status will update to Active.
        </p>
      )}

      {testResult && renderTestResult({ testResult, onClear: onClearTestResult, onRetry: onTestConnection })}
    </div>
  );
}

function renderOnboardingChecklist({
  artifacts, artifactsLoading, artifactsError,
  spVerify, spVerifying,
  clients, groupsSlugInput, onGroupsSlugChange,
  targetTenantId,
  onVerifyWorkforceApiSp, onCopy,
}) {
  // The four artifacts that have to be in place in the client tenant
  // before a federated user can sign in + be role-mapped. Listed in the
  // EXECUTION order — step 1 (SP-create) MUST run first because
  // consent-URL flows do NOT auto-provision a pure-resource-API SP in a
  // foreign tenant (CLAUDE.md Gotcha #51). Steps 2 & 3 then provide the
  // directory-read consent + sign-in consent. Step 4 creates the role
  // groups (per client — see below).
  //
  // Step 4 is per-CLIENT in a per-TENANT UI: the resolved slug is
  // sanitised live, and the script is filled from React state (no
  // backend round-trip). TARGET_TENANT_ID is the federated client
  // tenant GUID (config.externalTenantId).
  const resolvedSlug = slugifyClientName(groupsSlugInput);
  const groupsScript = resolvedSlug
    ? fillGroupsScript({ slug: resolvedSlug, tenantId: targetTenantId || '' })
    : '';
  const clientCount = Array.isArray(clients) ? clients.length : 0;
  const showMultiClientNote = clientCount > 1;

  return (
    <section className="federation-tab__onboarding" aria-labelledby="federation-onboarding-heading">
      <h3 className="federation-tab__onboarding-title" id="federation-onboarding-heading">
        Client-tenant onboarding sequence
      </h3>
      <p className="federation-tab__onboarding-intro">
        These four steps must be completed in the client&apos;s M365 tenant before any
        federated user can sign in and be role-mapped. Steps 2 and 3 can be done by
        clicking the URL directly; steps 1 and 4 require the client tenant administrator
        to run a command / script in their az CLI.
      </p>

      {artifactsError && (
        <div className="federation-tab__form-error" role="alert">
          Could not load onboarding artifacts: {artifactsError}
        </div>
      )}

      {/* Step 1 — SP-provisioning command */}
      <div className="federation-tab__onboarding-step">
        <div className="federation-tab__onboarding-step-header">
          <span className="federation-tab__onboarding-step-num">1</span>
          <div>
            <h4 className="federation-tab__onboarding-step-title">Run this in the client tenant&apos;s terminal first</h4>
            <p className="federation-tab__onboarding-step-help">
              Provisions the Workforce API service principal in the client tenant.
              Idempotent — safe to re-run. Requires Application Administrator (or higher)
              in the client tenant.
            </p>
          </div>
        </div>
        {artifactsLoading && !artifacts ? (
          <StatusMessage as="p" variant="loading" className="federation-tab__onboarding-step-loading">Loading…</StatusMessage>
        ) : artifacts && artifacts.workforceApiSpProvisioningCommand ? (
          <>
            <div className="federation-tab__onboarding-artifact">
              <pre className="federation-tab__onboarding-code" data-testid="onboarding-sp-command">{artifacts.workforceApiSpProvisioningCommand}</pre>
              <button
                type="button"
                className="button-secondary federation-tab__onboarding-copy"
                onClick={() => onCopy(artifacts.workforceApiSpProvisioningCommand)}
                aria-label="Copy SP provisioning command"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
            <div className="federation-tab__onboarding-verify">
              <button
                type="button"
                className="button-secondary"
                onClick={onVerifyWorkforceApiSp}
                disabled={spVerifying}
                data-testid="verify-workforce-api-sp-button"
              >
                {spVerifying ? 'Verifying…' : 'Verify'}
              </button>
              {renderSpVerifyResult(spVerify)}
            </div>
          </>
        ) : (
          <p className="federation-tab__onboarding-step-loading">
            Onboarding command not available — FEDERATED_API_AUDIENCE is not yet armed
            on the Function App. See the federation re-architecture runbook.
          </p>
        )}
      </div>

      {/* Step 2 — Federation Workload consent URL */}
      <div className="federation-tab__onboarding-step">
        <div className="federation-tab__onboarding-step-header">
          <span className="federation-tab__onboarding-step-num">2</span>
          <div>
            <h4 className="federation-tab__onboarding-step-title">Grant directory-read access</h4>
            <p className="federation-tab__onboarding-step-help">
              Admin-consent URL for the Federation Workload app reg. The client
              tenant admin clicks this once to grant the GroupMember.Read.All,
              User.Read.All, and Application.Read.All Application permissions
              that power group enumeration, reconcile, and the verify check above.
            </p>
          </div>
        </div>
        {artifactsLoading && !artifacts ? (
          <StatusMessage as="p" variant="loading" className="federation-tab__onboarding-step-loading">Loading…</StatusMessage>
        ) : artifacts && artifacts.consentUrl ? (
          <div className="federation-tab__onboarding-artifact">
            <pre className="federation-tab__onboarding-code" data-testid="onboarding-consent-url">{artifacts.consentUrl}</pre>
            <button
              type="button"
              className="button-secondary federation-tab__onboarding-copy"
              onClick={() => onCopy(artifacts.consentUrl)}
              aria-label="Copy Federation Workload consent URL"
            >
              <Copy size={14} /> Copy
            </button>
            <a
              href={artifacts.consentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="button-secondary federation-tab__onboarding-open"
            >
              Open in new tab
            </a>
          </div>
        ) : null}
      </div>

      {/* Step 3 — Federated SPA consent URL */}
      <div className="federation-tab__onboarding-step">
        <div className="federation-tab__onboarding-step-header">
          <span className="federation-tab__onboarding-step-num">3</span>
          <div>
            <h4 className="federation-tab__onboarding-step-title">Enable sign-in</h4>
            <p className="federation-tab__onboarding-step-help">
              Admin-consent URL for the Federated Sign-In SPA app reg. The client
              tenant admin clicks this to allow their users to sign in to CareHub
              via their existing M365 identity.
            </p>
          </div>
        </div>
        {artifactsLoading && !artifacts ? (
          <StatusMessage as="p" variant="loading" className="federation-tab__onboarding-step-loading">Loading…</StatusMessage>
        ) : artifacts && artifacts.federatedSignInConsentUrl ? (
          <div className="federation-tab__onboarding-artifact">
            <pre className="federation-tab__onboarding-code" data-testid="onboarding-federated-sign-in-url">{artifacts.federatedSignInConsentUrl}</pre>
            <button
              type="button"
              className="button-secondary federation-tab__onboarding-copy"
              onClick={() => onCopy(artifacts.federatedSignInConsentUrl)}
              aria-label="Copy Federated SPA consent URL"
            >
              <Copy size={14} /> Copy
            </button>
            <a
              href={artifacts.federatedSignInConsentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="button-secondary federation-tab__onboarding-open"
            >
              Open in new tab
            </a>
          </div>
        ) : (
          <p className="federation-tab__onboarding-step-loading">
            Federated sign-in URL not available — FEDERATED_SPA_CLIENT_ID is not yet
            armed on the Function App.
          </p>
        )}
      </div>

      {/* Step 4 — Create role groups (per client) */}
      <div className="federation-tab__onboarding-step">
        <div className="federation-tab__onboarding-step-header">
          <span className="federation-tab__onboarding-step-num">4</span>
          <div>
            <h4 className="federation-tab__onboarding-step-title">Create role groups</h4>
            <p className="federation-tab__onboarding-step-help">
              Creates the eleven A2Z CareHub security groups in the client tenant. They are
              then mapped to platform roles separately, per client, from each client&apos;s
              Group Mappings tab. Run as a Global Administrator in the client tenant:{' '}
              <code>az login --tenant &lt;client-tenant-id&gt;</code> first, then paste the
              script into Cloud Shell / bash.
            </p>
          </div>
        </div>

        {showMultiClientNote && (
          <div className="federation-tab__onboarding-note" role="note" data-testid="groups-multiclient-note">
            <AlertCircle size={14} />
            <span>
              This tenant has {clientCount} clients. Groups are per client — run this step
              once per client, each with its own slug.
            </span>
          </div>
        )}

        <div className="federation-tab__groups-field">
          <label htmlFor="groups-slug-input" className="federation-tab__groups-label">
            Client slug
          </label>
          <input
            id="groups-slug-input"
            type="text"
            className="federation-tab__groups-input"
            value={groupsSlugInput}
            onChange={(e) => onGroupsSlugChange(sanitizeSlugInput(e.target.value))}
            placeholder="e.g. sc-redbridge"
            autoComplete="off"
            spellCheck={false}
            data-testid="groups-slug-input"
          />
          <p className="federation-tab__onboarding-step-help">
            Becomes part of every group name (<code>aad-a2zcarehub-&lt;slug&gt;-…</code>).
            Must be unique per client; for a shared tenant, give each client a different
            slug. Lowercased and hyphenated automatically.
          </p>
        </div>

        {resolvedSlug ? (
          <>
            <p className="federation-tab__groups-preview" data-testid="groups-slug-preview">
              Groups will be named <code>aad-a2zcarehub-{resolvedSlug}-…</code>
            </p>
            <div className="federation-tab__onboarding-artifact">
              <pre className="federation-tab__onboarding-code" data-testid="onboarding-groups-script">{groupsScript}</pre>
              <button
                type="button"
                className="button-secondary federation-tab__onboarding-copy"
                onClick={() => onCopy(groupsScript)}
                aria-label="Copy create-groups script"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </>
        ) : (
          <p className="federation-tab__onboarding-step-loading" data-testid="onboarding-groups-empty">
            Enter a client slug above to generate the script.
          </p>
        )}
      </div>
    </section>
  );
}

function renderSpVerifyResult(spVerify) {
  if (!spVerify) return null;
  if (spVerify.spExists === true) {
    return (
      <span className="federation-tab__onboarding-verify-result federation-tab__onboarding-verify-result--success" role="status">
        <CheckCircle2 size={14} /> Workforce API SP present in client tenant
        {spVerify.displayName ? ` (${spVerify.displayName})` : ''}.
      </span>
    );
  }
  // Error paths — none of them mutate federation state on the backend
  // (Gotcha #51 — the verifier is pure-read). UI surfaces an inline
  // message + the operator's next action, that's it.
  if (spVerify.error === 'permission_not_granted') {
    return (
      <span className="federation-tab__onboarding-verify-result federation-tab__onboarding-verify-result--warning" role="alert">
        <AlertTriangle size={14} /> {spVerify.message || 'Cannot verify — Application.Read.All is not yet granted. Re-run step 2 above as a client-tenant Global Administrator.'}
      </span>
    );
  }
  if (spVerify.error === 'graph_forbidden') {
    return (
      <span className="federation-tab__onboarding-verify-result federation-tab__onboarding-verify-result--error" role="alert">
        <AlertCircle size={14} /> {spVerify.message || 'Graph returned 403 — check the Federation Workload consent in this tenant (step 2 above).'}
      </span>
    );
  }
  if (spVerify.error === 'transient') {
    return (
      <span className="federation-tab__onboarding-verify-result federation-tab__onboarding-verify-result--warning" role="alert">
        <AlertTriangle size={14} /> {spVerify.message || 'Graph temporarily unavailable — retry.'}
      </span>
    );
  }
  if (spVerify.error === 'unknown') {
    return (
      <span className="federation-tab__onboarding-verify-result federation-tab__onboarding-verify-result--error" role="alert">
        <AlertCircle size={14} /> {spVerify.message || 'Verification failed. Check App Insights.'}
      </span>
    );
  }
  // spExists: false, no error code — the canonical "not yet provisioned" answer.
  return (
    <span className="federation-tab__onboarding-verify-result federation-tab__onboarding-verify-result--warning" role="alert">
      <AlertTriangle size={14} /> Not found — run the command above as a client-tenant Global Administrator first.
    </span>
  );
}

function renderTestResult({ testResult, onClear, onRetry }) {
  if (testResult.success === true) {
    return (
      <div className="federation-tab__test-result federation-tab__test-result--success" role="status">
        <CheckCircle2 size={16} />
        <span>Connection successful. Sample group count: {testResult.sampleGroupCount}.</span>
      </div>
    );
  }
  if (testResult.error === 'transient') {
    return (
      <div className="federation-tab__test-result federation-tab__test-result--warning" role="alert">
        <AlertTriangle size={16} />
        <span>{testResult.message}</span>
        <button type="button" className="button-secondary" onClick={onRetry}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }
  return (
    <div className="federation-tab__test-result federation-tab__test-result--error" role="alert">
      <AlertCircle size={16} />
      <span>{testResult.message || 'Unexpected error. Check App Insights.'}</span>
      <button type="button" className="button-link" onClick={onClear}>Dismiss</button>
    </div>
  );
}

function renderCreateForm({ form, formErrors, inFlight, onChange, onSubmit, onCancel }) {
  return (
    <form className="federation-tab__form" onSubmit={onSubmit} noValidate>
      <h2 className="federation-tab__form-title">Configure federation</h2>

      {formErrors._top && (
        <div className="federation-tab__form-error" role="alert">{formErrors._top}</div>
      )}

      <Field
        label="Client tenant directory (tenant) ID"
        help="GUID of the client's Microsoft 365 tenant."
        error={formErrors.externalTenantId}
        required
      >
        <input
          type="text"
          value={form.externalTenantId}
          onChange={(e) => onChange('externalTenantId', e.target.value)}
          placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
          autoComplete="off"
        />
      </Field>

      <Field
        label="Identity provider"
        help="Phase 3 will widen this to other identity providers."
      >
        <input type="text" value="Microsoft 365" readOnly disabled />
      </Field>

      <Field
        label="Federated email domains"
        error={formErrors.federatedDomains}
        required
      >
        <DomainChipInput
          value={form.federatedDomains}
          onChange={(next) => onChange('federatedDomains', next)}
          validate={validateDomain}
          placeholder="example.co.uk"
          helperText="Email domains of users who will sign in via federation. Add one or more by typing and pressing Enter. Each domain can only federate to one Tenant globally."
          ariaLabel="Federated email domains"
        />
      </Field>

      {formErrors.existingTenantId && (
        <div className="federation-tab__form-error" role="alert">
          A submitted domain is already configured for tenant{' '}
          <Link to={`/tenants/${formErrors.existingTenantId}`}>
            #{formErrors.existingTenantId}
          </Link>
          . Domains must be unique across all federation configurations.
        </div>
      )}

      <div className="federation-tab__form-actions">
        <button
          type="submit"
          className="button-primary"
          disabled={inFlight === 'updating'}
        >
          {inFlight === 'updating' ? 'Creating…' : 'Create configuration'}
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function renderEditForm({ form, formErrors, inFlight, initialConfig, onChange, onSubmit, onCancel }) {
  const hasChanges =
    form.externalTenantId !== initialConfig.externalTenantId
    || form.isActive !== initialConfig.isActive;

  return (
    <form className="federation-tab__form" onSubmit={onSubmit} noValidate>
      <h2 className="federation-tab__form-title">Edit federation configuration</h2>

      {formErrors._top && (
        <div className="federation-tab__form-error" role="alert">{formErrors._top}</div>
      )}

      <Field
        label="Client tenant directory (tenant) ID"
        help="GUID of the client's Microsoft 365 tenant."
        error={formErrors.externalTenantId}
        required
      >
        <input
          type="text"
          value={form.externalTenantId}
          onChange={(e) => onChange('externalTenantId', e.target.value)}
          autoComplete="off"
        />
      </Field>

      <Field
        label="Identity provider"
        help="Cannot be changed after creation."
      >
        <input type="text" value="Microsoft 365" readOnly disabled />
      </Field>

      <p className="federation-tab__hint">
        Domains are managed individually from the active-state view via the
        "Add domain" button and per-row remove actions, not from this form.
      </p>

      <Field label="Active">
        <label className="federation-tab__toggle">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => onChange('isActive', e.target.checked)}
          />
          <span>Federation is active</span>
        </label>
      </Field>

      <div className="federation-tab__form-actions">
        <button
          type="submit"
          className="button-primary"
          disabled={inFlight === 'updating' || !hasChanges}
        >
          {inFlight === 'updating' ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
